import apiInstance from '@api';
import { JiraIssueFields } from '@api/support/SupportAPI';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { FileInputValue } from '../../../FileUploader/types';
import FormFieldsContainer from '../../../Form/components/FormFieldContainer';
import SchemaRenderer from '../../../Form/components/SchemaRenderer';
import toastMessage from '../../../Toast';

import {
  MAX_ATTACHMENT_SIZE,
  MAX_ATTACHMENTS,
  NO_ERROR_MESSAGE,
  TASK_DEFAULT_VALUES,
} from '../../constants';
import { StyledFileUploader } from '../../styles';
import { TaskRequestFormProps, TaskRequestFormValues } from '../../types';
import { buildTaskRequestFormSchema } from '../../utils';

import FormActions from '../FormActions';

// Bug / change request / new feature / data issue / question: a single
// multipart call creates the Jira issue, attachments and Teamwork task.
const TaskRequestForm: FC<TaskRequestFormProps> = ({
  requestType,
  onCancel,
  onSubmitted,
}) => {
  const [files, setFiles] = useState<FileInputValue[]>([]);
  const [filesError, setFilesError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(() => buildTaskRequestFormSchema(), []);
  const methods = useForm<TaskRequestFormValues>({
    mode: 'onSubmit',
    defaultValues: TASK_DEFAULT_VALUES,
  });

  const handleSubmit = useCallback(
    async (values: TaskRequestFormValues) => {
      const attachments = files
        .map((file) => file.data)
        .filter((file): file is File => Boolean(file));

      if (attachments.length > MAX_ATTACHMENTS) {
        setFilesError(`You can attach at most ${MAX_ATTACHMENTS} files.`);
        return;
      }

      setSubmitting(true);

      const payload: JiraIssueFields = {
        request_type: requestType,
        title: values.report_title.trim(),
        description: values.report_description.trim(),
        page_url: window.location.href,
        error_message: values.error_message.trim() || NO_ERROR_MESSAGE,
        additional_context: values.additional_context.trim(),
        user_details: {
          userAgent: window.navigator.userAgent,
          referrer: document.referrer,
        },
        attachments,
      };

      try {
        await apiInstance.support.createJiraIssue(payload);

        toastMessage.success('Your request has been submitted. Thank you!');
        onSubmitted();
      } catch (error) {
        console.error('Failed to submit the change request:', error);
        toastMessage.error(
          'Failed to submit your request. Please try again later.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [files, onSubmitted, requestType],
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormFieldsContainer columns={1}>
          <SchemaRenderer schema={schema} />
        </FormFieldsContainer>
        <StyledFileUploader
          label="Attachments"
          name="change_request_attachments"
          multi
          maxSize={MAX_ATTACHMENT_SIZE}
          description="Screenshots or files that help us understand the request."
          error={filesError}
          setError={setFilesError}
          clearError={() => setFilesError(undefined)}
          hideSubmitButton
          value={files}
          onChange={setFiles}
        />
        <FormActions submitting={submitting} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
};

export default TaskRequestForm;
